import type { EmployeeAchievementsResponse, SourceTypeCode } from '../../api/types';
import { formatCalendarDate } from './factDisplay';
import { EditAchievementControl } from './EmployeeFactsCapture';
import styles from './FactRecords.module.css';

/**
 * The Achievements section (docs/DOMAIN_MODEL.md §2.10).
 *
 * ---------------------------------------------------------------------
 * AN ACHIEVEMENT IS NOT AN ASSESSMENT
 *
 * The domain model left Achievement deliberately unstructured — a title, a
 * description, a date, and the role or team it happened in — because
 * nothing in the system mechanically matches against one. That absence of
 * structure is a decision, not a gap waiting to be filled by the UI.
 *
 * So this section shows records, in the order the API returned them (most
 * recent first), and does nothing else. Specifically it does not:
 *
 * - score, rate, weight or star them;
 * - rank them by importance, impact or anything else;
 * - count them into a headline ("5 achievements" as a measure of a person
 *   rather than of a list);
 * - infer capability, potential, readiness or talent from them
 *   (docs/DERIVED_LOGIC.md §19a is explicit that nothing does);
 * - summarise or interpret them (docs/PRODUCT_CONSTITUTION.md §22 puts AI
 *   interpretation outside the MVP entirely).
 *
 * Whether an achievement means anything is a judgement, and the person
 * making it needs the record, its date and its context — not a number
 * standing in for all three.
 *
 * The related role and team are shown because an outcome is only legible
 * in context: "resolved a recurring feeder fault" reads differently for a
 * technician and for a network engineer.
 * ---------------------------------------------------------------------
 */
export function AchievementsSection({
  data,
  employeeId,
  viewerCanAssessAs,
  onChanged,
}: {
  data: EmployeeAchievementsResponse;
  employeeId: string;
  viewerCanAssessAs: SourceTypeCode | null;
  onChanged: () => void;
}) {
  return (
    <ul className={styles.list}>
      {data.achievements.map((entry) => (
        <li key={entry.id} className={styles.record}>
          <div className={styles.primary}>
            <span className={styles.name}>{entry.title}</span>
            <span className={styles.category}>
              {entry.occurredOn === null ? 'Date not recorded' : formatCalendarDate(entry.occurredOn)}
            </span>
          </div>

          {entry.description ? <p className={styles.notes}>{entry.description}</p> : null}

          {entry.relatedRole || entry.relatedTeam ? (
            <dl className={styles.facts}>
              {entry.relatedRole ? (
                <div>
                  <dt>Role at the time</dt>
                  <dd>{entry.relatedRole.name}</dd>
                </div>
              ) : null}
              {entry.relatedTeam ? (
                <div>
                  <dt>Team at the time</dt>
                  <dd>{entry.relatedTeam.name}</dd>
                </div>
              ) : null}
            </dl>
          ) : null}

          {viewerCanAssessAs !== null ? (
            <EditAchievementControl employeeId={employeeId} achievement={entry} onChanged={onChanged} />
          ) : null}
        </li>
      ))}
    </ul>
  );
}
